import { useState, useCallback } from 'react';
import IntroSection from './components/IntroSection';
import BirthDateInput from './components/BirthDateInput';
import BirthTimeInput from './components/BirthTimeInput';
import BirthLocationInput from './components/BirthLocationInput';
import LoadingAnimation from './components/LoadingAnimation';
import ResultReceipt from './components/ResultReceipt';
import SajuResultView from './components/SajuResult';
import SajuHistory from './components/SajuHistory';
import { calculateCorrection, calculateSaju } from '@/utils/sajuCalculations';
import type { CorrectionResult, SajuResult } from '@/utils/sajuCalculations';
import { lunarToSolar } from '@/utils/lunarCalendar';
import type { LocationData } from './components/BirthLocationInput';
import type { SajuHistoryItem } from '@/hooks/useSajuHistory';

type CalendarType = 'solar' | 'lunar' | 'lunarLeap';
type Gender = 'male' | 'female';
type Step = 'intro' | 'date' | 'time' | 'location' | 'loading' | 'receipt' | 'result';

interface FormData {
  year: number;
  month: number;
  day: number;
  calendarType: CalendarType;
  gender: Gender;
  hour: number;
  minute: number;
  unknownTime: boolean;
  location: LocationData | null;
}

export interface LunarConversionInfo {
  lunarYear: number;
  lunarMonth: number;
  lunarDay: number;
  calendarType: CalendarType;
  solarYear: number;
  solarMonth: number;
  solarDay: number;
}

const DEFAULT_FORM: FormData = {
  year: 1990,
  month: 6,
  day: 15,
  calendarType: 'solar',
  gender: 'female',
  hour: 14,
  minute: 0,
  unknownTime: false,
  location: null,
};

export default function Home() {
  const [step, setStep] = useState<Step>('intro');
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [correction, setCorrection] = useState<CorrectionResult | null>(null);
  const [sajuResult, setSajuResult] = useState<SajuResult | null>(null);
  const [lunarConvInfo, setLunarConvInfo] = useState<LunarConversionInfo | null>(null);
  const [solarDate, setSolarDate] = useState<{ year: number; month: number; day: number } | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const goToStep = useCallback((s: Step) => {
    setStep(s);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 히스토리에서 선택한 사주 불러오기
  const handleHistorySelect = (item: SajuHistoryItem) => {
    setForm({
      year: item.year,
      month: item.month,
      day: item.day,
      calendarType: 'solar',
      gender: 'female',
      hour: item.hour ?? 12,
      minute: item.minute,
      unknownTime: item.unknownTime,
      location: null,
    });
    setSajuResult(item.saju);
    setSolarDate({ year: item.year, month: item.month, day: item.day });
    setLunarConvInfo(null);
    setCorrection({
      inputHour: item.hour ?? 12,
      inputMinute: item.minute,
      correctedHour: item.hour ?? 12,
      correctedMinute: item.minute,
      eotCorrection: 0,
      longitudeCorrection: 0,
      dstCorrection: 0,
      dstActive: false,
      totalCorrectionMinutes: 0,
      timezoneNote: '',
      inputBranch: '',
      correctedBranch: '',
      branchChanged: false,
    });
    setShowHistory(false);
    goToStep('result');
  };

  const handleDateNext = (data: { year: number; month: number; day: number; calendarType: CalendarType; gender: Gender }) => {
    setForm(f => ({ ...f, ...data }));
    goToStep('time');
  };

  const handleTimeNext = (data: { hour: number; minute: number; unknownTime: boolean }) => {
    setForm(f => ({ ...f, ...data }));
    goToStep('location');
  };

  const handleLocationNext = (location: LocationData) => {
    const updatedForm = { ...form, location };
    setForm(updatedForm);

    // ✅ 음력/음력 윤달 선택 시 반드시 양력으로 변환 후 계산 엔진에 전달
    let solarYear  = updatedForm.year;
    let solarMonth = updatedForm.month;
    let solarDay   = updatedForm.day;

    if (updatedForm.calendarType === 'lunar' || updatedForm.calendarType === 'lunarLeap') {
      const isLeap    = updatedForm.calendarType === 'lunarLeap';
      const converted = lunarToSolar(updatedForm.year, updatedForm.month, updatedForm.day, isLeap);
      if (converted) {
        solarYear  = converted.year;
        solarMonth = converted.month;
        solarDay   = converted.day;
        setLunarConvInfo({
          lunarYear: updatedForm.year, lunarMonth: updatedForm.month, lunarDay: updatedForm.day,
          calendarType: updatedForm.calendarType,
          solarYear, solarMonth, solarDay,
        });
      } else {
        // 변환 실패 시 음력 날짜를 그대로 사주 계산에 쓰지 않도록 안전 처리
        // (균시차·경도 보정은 반드시 양력 날짜 기준으로 계산해야 함)
        console.error(
          '[바른사주] lunarToSolar 변환 실패:',
          updatedForm.year, updatedForm.month, updatedForm.day, isLeap
        );
        setLunarConvInfo(null);
      }
    } else {
      setLunarConvInfo(null);
    }
    setSolarDate({ year: solarYear, month: solarMonth, day: solarDay });

    const hourToUse   = updatedForm.unknownTime ? 12 : updatedForm.hour;
    const minuteToUse = updatedForm.unknownTime ? 0  : updatedForm.minute;

    const correctionData = calculateCorrection(
      solarYear,
      solarMonth,
      solarDay,
      hourToUse,
      minuteToUse,
      location.longitude
    );
    setCorrection(correctionData);

    const saju = calculateSaju(
      solarYear,
      solarMonth,
      solarDay,
      updatedForm.unknownTime ? null : correctionData.correctedHour,
      correctionData.correctedMinute,
      updatedForm.unknownTime,
      true
    );
    setSajuResult(saju);

    goToStep('loading');
  };

  const handleLoadingComplete = () => {
    if (form.unknownTime) {
      goToStep('result');
    } else {
      goToStep('receipt');
    }
  };

  const handleRestart = () => {
    setForm(DEFAULT_FORM);
    setCorrection(null);
    setSajuResult(null);
    setLunarConvInfo(null);
    setSolarDate(null);
    goToStep('intro');
  };

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return <IntroSection onStart={() => goToStep('date')} />;

      case 'date':
        return (
          <BirthDateInput
            onNext={handleDateNext}
            defaultYear={form.year}
            defaultMonth={form.month}
            defaultDay={form.day}
            defaultCalendarType={form.calendarType}
            defaultGender={form.gender}
          />
        );

      case 'time':
        return (
          <BirthTimeInput
            onNext={handleTimeNext}
            onBack={() => goToStep('date')}
            defaultHour={form.hour}
            defaultMinute={form.minute}
          />
        );

      case 'location':
        return (
          <BirthLocationInput
            onNext={handleLocationNext}
            onBack={() => goToStep('time')}
          />
        );

      case 'loading':
        return (
          <LoadingAnimation
            year={form.year}
            locationName={form.location?.name ?? ''}
            onComplete={handleLoadingComplete}
          />
        );

      case 'receipt':
        return correction && form.location ? (
          <ResultReceipt
            correctionData={correction}
            year={form.year}
            locationName={form.location.name}
            locationAddress={form.location.address}
            onNext={() => goToStep('result')}
          />
        ) : null;

      case 'result':
        return sajuResult && correction && solarDate ? (
          <SajuResultView
            result={sajuResult}
            correctedHour={correction.correctedHour}
            correctedMinute={correction.correctedMinute}
            longitude={form.location?.longitude ?? 135}
            eotCorrection={correction.eotCorrection}
            unknownTime={form.unknownTime}
            gender={form.gender}
            solarYear={solarDate.year}
            solarMonth={solarDate.month}
            solarDay={solarDate.day}
            lunarConvInfo={lunarConvInfo}
            locationName={form.location?.name ?? '현재 위치'}
            onRestart={handleRestart}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-dvh bg-white">
      {/* 히스토리 버튼 - intro 단계에서만 표시 */}
      {step === 'intro' && (
        <button
          onClick={() => setShowHistory(true)}
          className="fixed top-4 right-4 z-40 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <i className="ri-history-line text-gray-700" />
        </button>
      )}

      {renderStep()}

      {/* 히스토리 모달 */}
      {showHistory && (
        <SajuHistory
          onSelect={handleHistorySelect}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
